import type { ApiProblem } from '@rental/contracts/client'
import { type CaptureItem, type InspectionKey, sameInspection } from './item'

/**
 * S4 (OPS-5): the queue's rules, pure, so they are tested without a browser.
 * The drainer (drainer.ts) applies them; the store (store.ts) keeps the items.
 */

/** The wait before trying an item again: 5 s, 15 s, 30 s, 1 min, then every 2 min. */
export const BACKOFF_MS = [5_000, 15_000, 30_000, 60_000, 120_000] as const

export function backoffMs(attempts: number): number {
  const index = Math.min(Math.max(attempts, 1), BACKOFF_MS.length) - 1
  return BACKOFF_MS[index] ?? 120_000
}

/** A request with no answer by then counts as a dropped connection, and the queue moves on. */
export const POST_TIMEOUT_MS = 15_000

/** A photo's bytes get longer: 20 s, plus a second for every 25 kB, at most 3 minutes. */
export function putTimeoutMs(sizeBytes: number): number {
  return Math.min(180_000, 20_000 + Math.ceil(sizeBytes / 25_000) * 1_000)
}

/** Unsent for longer than this: the shell says so on every screen (ARCHITECTURE). */
export const STALE_AFTER_MS = 15 * 60_000

/** A sent item no inspection screen has listed yet is let go after this (the server has it). */
export const SENT_KEPT_MS = 10 * 60_000

/** How the queue reads a request that failed. */
export type Verdict =
  /**
   * No answer, the server busy or failing: by itself, later. `stopPass` when
   * every other item would fail the same way now (no connection at all).
   */
  | { kind: 'later'; stopPass: boolean }
  /** The session, the device or the app version moved: the screens' takeOver decides. */
  | { kind: 'access' }
  /** The photo's link ran out or was refused: ask again for the same photo, once. */
  | { kind: 'renew' }
  /** The bytes arrived already, on an earlier send: asking again confirms it. */
  | { kind: 'stored' }
  /** Refused as it is. Riprova is offered only where trying again can succeed. */
  | { kind: 'refused'; retryable: boolean }

export function verdictOf(problem: ApiProblem): Verdict {
  // No connection, or a timeout: every other item would fail the same way now.
  if (problem.kind === 'network') return { kind: 'later', stopPass: true }
  const { status, code } = problem
  if (status === 429) return { kind: 'later', stopPass: true }
  if (status >= 500) return { kind: 'later', stopPass: false }
  if (status === 401 && code === 'upload_link_invalid') return { kind: 'renew' }
  if (status === 401 || status === 426) return { kind: 'access' }
  if (status === 409 && code === 'already_uploaded') return { kind: 'stored' }
  // The app ahead of its API (S4a deploys first): once the API has caught up, it goes.
  if (status === 400) return { kind: 'refused', retryable: true }
  // A closed stage, another device, other details, a limit, a role, a lost booking.
  return { kind: 'refused', retryable: false }
}

/** Unsent: not yet on the server, whatever the reason. */
export function unsent(item: CaptureItem): boolean {
  return item.state !== 'sent'
}

const byCapture = (a: CaptureItem, b: CaptureItem) =>
  a.capturedAt - b.capturedAt || a.id.localeCompare(b.id)

/** Fewest failed tries first, then the oldest: an item that keeps failing never starves the rest. */
const bySendOrder = (a: CaptureItem, b: CaptureItem) => a.attempts - b.attempts || byCapture(a, b)

/**
 * A photo taken of a damage still on this iPad, as it is kept: when that
 * damage has reached the server meanwhile, the photo names it by the
 * server's id at once (read in the same transaction as the write).
 *
 * `letGo`: the damages already let go from the iPad (`damagesLetGo`), by their
 * item. A photo is converted before it is kept, with the target it was chosen
 * with: its damage may have been sent, listed and let go meanwhile, and
 * without this the photo would wait for it for good, holding the return
 * (found in S9's gate).
 */
export function linkToDamage<T extends CaptureItem>(
  item: T,
  items: readonly CaptureItem[],
  letGo: ReadonlyMap<string, string> = new Map(),
): T {
  if (item.kind !== 'photo' || item.damageId || !item.damageItemId) return item
  const damage = items.find((candidate) => candidate.id === item.damageItemId)
  if (damage?.state === 'sent' && damage.serverId) return { ...item, damageId: damage.serverId }
  const known = damage ? undefined : letGo.get(item.damageItemId)
  return known ? { ...item, damageId: known } : item
}

/** Of the items `settled` lets go, the sent damages, by item, with the server's id. */
export function damagesLetGo(
  items: readonly CaptureItem[],
  gone: readonly string[],
): [itemId: string, serverId: string][] {
  const leaving = new Set(gone)
  return items.flatMap((item) =>
    item.kind === 'damage' && item.serverId && leaving.has(item.id)
      ? [[item.id, item.serverId] as [string, string]]
      : [],
  )
}

/** The damage a photo shows, by the server's id: known once that damage is recorded. */
export function damageIdOf(
  photo: CaptureItem,
  byId: ReadonlyMap<string, CaptureItem>,
): string | null {
  if (photo.kind !== 'photo') return null
  if (photo.damageId) return photo.damageId
  if (!photo.damageItemId) return null
  const damage = byId.get(photo.damageItemId)
  return damage?.state === 'sent' ? damage.serverId : null
}

/** A photo of a damage still on this iPad waits for that damage. */
export function waitsForDamage(item: CaptureItem, byId: ReadonlyMap<string, CaptureItem>) {
  return item.kind === 'photo' && item.damageItemId !== null && damageIdOf(item, byId) === null
}

/**
 * The next item to send for the person unlocked: only theirs, only when its
 * wait is over, a damage before the photos that name it (they wait for it),
 * the fewest failed tries first, then the oldest.
 */
export function nextToSend(
  items: readonly CaptureItem[],
  userId: string,
  now: number,
): CaptureItem | null {
  const byId = new Map(items.map((item) => [item.id, item]))
  const ready = items
    .filter(
      (item) =>
        item.state === 'queued' &&
        item.capturedBy.userId === userId &&
        item.nextAttemptAt <= now &&
        !waitsForDamage(item, byId),
    )
    .sort(bySendOrder)
  return ready[0] ?? null
}

/** When this person's next item comes due, if one is waiting out its backoff. */
export function nextWakeAt(items: readonly CaptureItem[], userId: string): number | null {
  const byId = new Map(items.map((item) => [item.id, item]))
  let earliest: number | null = null
  for (const item of items) {
    if (item.state !== 'queued' || item.capturedBy.userId !== userId) continue
    if (waitsForDamage(item, byId)) continue
    if (earliest === null || item.nextAttemptAt < earliest) earliest = item.nextAttemptAt
  }
  return earliest
}

/** What a hand-over or a return waits for: this iPad's items of its inspection not yet on the server. */
export function unsentFor(items: readonly CaptureItem[], key: InspectionKey): CaptureItem[] {
  return items.filter((item) => sameInspection(item, key) && unsent(item)).sort(byCapture)
}

/**
 * At a hand-over, items of the same booking and stage for another vehicle
 * (one considered and not chosen): listed, so they are not forgotten, but
 * they never hold up the vehicle that leaves.
 */
export function otherVehicles(items: readonly CaptureItem[], key: InspectionKey): CaptureItem[] {
  const code = key.bookingCode.toUpperCase()
  return items
    .filter(
      (item) =>
        item.bookingCode === code &&
        item.stage === key.stage &&
        item.vehicleId !== key.vehicleId &&
        unsent(item),
    )
    .sort(byCapture)
}

/** Discarding a damage takes the photos of it still on this iPad with it. */
export function withDependents(items: readonly CaptureItem[], ids: readonly string[]): string[] {
  const gone = new Set(ids)
  for (const item of items) {
    if (item.kind === 'photo' && item.damageItemId && gone.has(item.damageItemId)) gone.add(item.id)
  }
  return [...gone]
}

/**
 * Sent items that can leave the iPad: those the server's inspection now
 * lists, and those sent long enough ago that no screen is waiting for them.
 */
export function settled(
  items: readonly CaptureItem[],
  listed: { photos: ReadonlySet<string>; damages: ReadonlySet<string> } | null,
  now: number,
): string[] {
  // A damage stays while a photo of it still names it only by its item: the
  // photo learns the damage's server id from it.
  const named = new Set(
    items.flatMap((item) =>
      item.kind === 'photo' && item.damageItemId && !item.damageId ? [item.damageItemId] : [],
    ),
  )
  return items
    .filter((item) => {
      if (item.state !== 'sent' || named.has(item.id)) return false
      if (item.sentAt !== null && now - item.sentAt >= SENT_KEPT_MS) return true
      if (!listed || !item.serverId) return false
      return item.kind === 'photo'
        ? listed.photos.has(item.serverId)
        : listed.damages.has(item.serverId)
    })
    .map((item) => item.id)
}

export interface QueueSummary {
  /** Not yet on the server: waiting, on its way, or refused. */
  unsent: number
  refused: number
  /** When the oldest unsent item was taken. */
  oldestAt: number | null
  /** Whose they are: each is sent only while its person is unlocked. */
  people: { userId: string; name: string; unsent: number }[]
}

export function summarize(items: readonly CaptureItem[]): QueueSummary {
  const people = new Map<string, { userId: string; name: string; unsent: number }>()
  let count = 0
  let refused = 0
  let oldestAt: number | null = null
  for (const item of items) {
    if (!unsent(item)) continue
    count++
    if (item.state === 'refused') refused++
    if (oldestAt === null || item.capturedAt < oldestAt) oldestAt = item.capturedAt
    const person = people.get(item.capturedBy.userId) ?? { ...item.capturedBy, unsent: 0 }
    person.unsent++
    people.set(item.capturedBy.userId, person)
  }
  return { unsent: count, refused, oldestAt, people: [...people.values()] }
}

/** True when something has waited past STALE_AFTER_MS: the banner shows. */
export function isStale(summary: QueueSummary, now: number): boolean {
  return summary.oldestAt !== null && now - summary.oldestAt > STALE_AFTER_MS
}
